/**
 * @bos/portal3 — the runs query/stream layer (batch 4), the projects/documents hooks'
 * sibling: little useState + effects around the typed tRPC client, page-scoped.
 *
 *   - useRun(id)            → one run's live view: seed from `runs.get` (row + full
 *     journal), THEN attach the SSE stream from the last sequence already held — never
 *     `after=0` on a re-mount, or the whole history re-delivers. Frames fold through the
 *     pure, idempotent reducer (model.ts). The client owns the hang-up: the stream is
 *     closed only when the view reaches a terminal status ('awaiting-owner' is LIVE and
 *     keeps the stream open — an answered round auto-resumes on the SAME run id and the
 *     same connection carries the continuation).
 *   - useRunQuestions(...)  → the CURRENT question round via `runs.questions` (question
 *     text does not ride the frames), plus the answer door.
 *   - useProjectRuns(...)   → a business's runs (every app's) — the live-run signal.
 *   - useAppRuns(...)       → ONE app's builds, newest first: its own history.
 *   - useStageReview(...)   → ONE of the six batch-7 stage read surfaces (lifecycles /
 *     operations / rules / permissions / audit / modules), fetched LAZILY — only when a
 *     person opens that stage from the rail — and re-read when the run's status moves.
 *     Absence is a payload state ('absent' / 'unintelligible'), never an error.
 *   - useRunDocuments(...) → the run's DOCUMENT RECORD via `runs.documents`: which of the
 *     project's documents were read into this build's brief and which were held, with the
 *     reason. Fetched once — the record is frozen at launch and never moves.
 *   - useRunAppPackage(...) → the HANDOVER: whether the machine actually left an app on
 *     disk for this run, what the archive would carry, and how to install and start it —
 *     every fact measured server-side out of the app's own bytes. `appPackageUrl(id)` is
 *     the same-origin download link an `<a href>` points at.
 *   - useRunCode(...) / useCodeFile(...) → the CODE room's two reads: the modules the
 *     machine wrote with their files, and then ONE file's text. The client never composes
 *     a path — it sends back one the listing handed out — and the server re-jails it
 *     regardless; a refusal comes back as a payload state with its own sentence.
 *   - useRunScreens(...) → the SCREENS room's read: the generated screen spec joined with
 *     the machine's own render check, per screen. Absence is a state, never an error.
 *
 * TRANSPORT LAW (batch 3 hazard #1): tRPC speaks superjson; the SSE stream speaks PLAIN
 * JSON. Stream payloads go through `JSON.parse`, never the tRPC transformer.
 *
 * RECONNECT LAW (hazard #2): the stream never closes itself, and a drop is the NORMAL
 * case — EventSource auto-reconnects carrying `Last-Event-ID`, so the server replays
 * exactly what was missed. A reconnect must never render as an error; while the browser
 * retries we show a quiet "reconnecting" state, and a fatally-closed stream re-attaches
 * from the held cursor after a short pause.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { TRPCClientError } from '@trpc/client';
import type {
  AnswerRunResult,
  AppPackageRead,
  ApplyDataModelBasketResult,
  ApproveDataModelResult,
  AuditDoc,
  CheckpointAnswer,
  CodeFileEntry,
  CodeFileRead,
  CodeHole,
  CodeModuleEntry,
  CodeModulesRead,
  NavEdgeDoc,
  ScreenDoc,
  ScreenPreviewRead,
  ScreensCheckDoc,
  ScreensDoc,
  ScreensPreviewsDoc,
  DataModelEdit,
  EditDataModelResult,
  LifecyclesDoc,
  ModulesDoc,
  OperationsDoc,
  PermissionsDoc,
  RequestDataModelRevisionResult,
  RequestStageRevisionResult,
  RevisionComment,
  Run,
  RunStageDoorResult,
  ApproveStageRouteResult,
  StageEditPatch,
  RunStageEditPreview,
  OrderStageEditResult,
  RunStageBasketItem,
  RunStageBasketPreview,
  ApplyStageBasketResult,
  RulesDoc,
  RunDataModelBasketItem,
  RunDataModelBasketPreview,
  RunDataModelDiffResult,
  RunDataModelEditPreview,
  RunDataModelResult,
  RunDocumentsResult,
  RunEventRecord,
  RunIntakeSurface,
  RunOwnerEditsResult,
  RunQuestionsResult,
  OwnerLedgerHistoryRow,
  RetractEditResult,
  StageRead,
  VerdictDoc,
} from '@bos/portal3-server';
import { client } from '../rpc/client';
import {
  applyFrame,
  emptyRunView,
  isTerminalStatus,
  seedRunView,
  withRun,
  type RunView,
  type StageReviewKey,
} from './model';

export type {
  Run,
  RunQuestionsResult,
  AnswerRunResult,
  CheckpointAnswer,
  RunDataModelResult,
  ApproveDataModelResult,
  RunDataModelDiffResult,
  RevisionComment,
  RequestDataModelRevisionResult,
  DataModelEdit,
  RunDataModelEditPreview,
  EditDataModelResult,
  RunDataModelBasketItem,
  RunDataModelBasketPreview,
  ApplyDataModelBasketResult,
  RunStageDoorResult,
  ApproveStageRouteResult,
  RequestStageRevisionResult,
  StageEditPatch,
  RunStageEditPreview,
  OrderStageEditResult,
  RunStageBasketItem,
  RunStageBasketPreview,
  ApplyStageBasketResult,
  StageRead,
  LifecyclesDoc,
  OperationsDoc,
  RulesDoc,
  PermissionsDoc,
  AuditDoc,
  ModulesDoc,
  VerdictDoc,
  CodeModulesRead,
  CodeModuleEntry,
  CodeFileEntry,
  CodeFileRead,
  CodeHole,
  ScreensDoc,
  ScreenDoc,
  ScreenPreviewRead,
  ScreensCheckDoc,
  ScreensPreviewsDoc,
  NavEdgeDoc,
  RunDocumentsResult,
  RunOwnerEditsResult,
  OwnerLedgerHistoryRow,
  RetractEditResult,
};

/** Turn a thrown tRPC error into a message safe to render (zod issues → first message). */
export function runErrorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) {
    const raw: string = err.message ?? '';
    try {
      const issues = JSON.parse(raw) as Array<{ message?: string }>;
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      /* not a zod issue array — fall through to the plain message */
    }
    return raw || 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Please try again.';
}

/** True when a thrown tRPC error is a NOT_FOUND (missing OR not the caller's — same signal). */
function isNotFound(err: unknown): boolean {
  return err instanceof TRPCClientError && err.data?.code === 'NOT_FOUND';
}

/** The start conflict, parsed: the server names the run already going in its message. */
export interface StartConflict {
  readonly runId: string | null;
  readonly message: string;
}

/** Classify a `runs.start` refusal: a CONFLICT is INFORMATION (a run is already going —
 *  here it is), never an error. Returns null for anything that is a real error. */
export function startConflictOf(err: unknown): StartConflict | null {
  if (err instanceof TRPCClientError && err.data?.code === 'CONFLICT') {
    const m = /r_[A-Za-z0-9_-]+/.exec(err.message ?? '');
    return { runId: m ? m[0] : null, message: err.message ?? '' };
  }
  return null;
}

// ── one run, live (the workbench) ────────────────────────────────────────────

export type RunLoadStatus = 'loading' | 'ready' | 'not-found' | 'error';
/** 'open' = frames flowing · 'reconnecting' = a normal drop being healed · 'idle' = no stream (terminal or not yet ready). */
export type StreamState = 'idle' | 'open' | 'reconnecting';

export type ResumeOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly kind: 'winding-down' | 'refused'; readonly message: string };

export interface UseRun {
  readonly view: RunView;
  readonly status: RunLoadStatus;
  readonly error: string | null;
  readonly streamState: StreamState;
  reload: () => Promise<void>;
  /** Stop the run. Resolves `true` when this call stopped it, `false` when there was
   *  nothing to stop (already stopped / finished) — a NO-OP, never an error. */
  stop: () => Promise<boolean>;
  /** Resume the run. A winding-down refusal is a try-again-shortly, not an error. */
  resume: () => Promise<ResumeOutcome>;
}

export function useRun(id: string): UseRun {
  const [view, setView] = useState<RunView>(emptyRunView);
  const [status, setStatus] = useState<RunLoadStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [streamState, setStreamState] = useState<StreamState>('idle');
  const [retryNonce, setRetryNonce] = useState(0);

  // The attach cursor lives in a ref so the stream effect reads the LATEST folded seq
  // without re-running on every frame.
  const viewRef = useRef(view);
  viewRef.current = view;

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      // Seed from the journal FIRST; the SSE attach below starts from its last seq.
      const payload = await client.runs.get.query({ id });
      setView(seedRunView(payload));
      setStatus('ready');
    } catch (err) {
      if (isNotFound(err)) {
        setView(emptyRunView);
        setStatus('not-found');
      } else {
        setError(runErrorMessage(err));
        setStatus('error');
      }
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Stream while the run is LIVE ('running' or 'awaiting-owner'). Terminal → the CLIENT
  // hangs up (the stream never closes itself). A resume flips status back to 'running',
  // which re-runs this effect and re-attaches from the held cursor.
  const streaming = status === 'ready' && view.status !== null && !isTerminalStatus(view.status);

  useEffect(() => {
    if (!streaming) {
      setStreamState('idle');
      return;
    }
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    // Attach from the last sequence already held — NEVER after=0 once a journal is folded.
    const es = new EventSource(
      `/api/runs/${encodeURIComponent(id)}/events?after=${viewRef.current.lastSeq}`,
    );
    es.onopen = () => {
      if (!disposed) setStreamState('open');
    };
    es.onmessage = (e: MessageEvent) => {
      try {
        // PLAIN JSON — the stream never speaks superjson (batch 3 hazard #1).
        const record = JSON.parse(e.data as string) as RunEventRecord;
        if (typeof record === 'object' && record !== null && typeof record.seq === 'number') {
          setView((v) => applyFrame(v, record));
        }
      } catch {
        /* a malformed frame is dropped; the journal remains the truth */
      }
    };
    es.onerror = () => {
      if (disposed) return;
      // A drop is NORMAL: EventSource reconnects itself with Last-Event-ID. Show a quiet
      // healing state, never an error.
      setStreamState('reconnecting');
      if (es.readyState === 2 /* EventSource.CLOSED */ && retryTimer === undefined) {
        // The browser gave up (fatal close). Re-attach manually from the held cursor.
        retryTimer = setTimeout(() => setRetryNonce((n) => n + 1), 3000);
      }
    };

    return () => {
      disposed = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      es.close();
    };
  }, [id, streaming, retryNonce]);

  const stop = useCallback(async (): Promise<boolean> => {
    const res = await client.runs.stop.mutate({ id });
    if (res.stopped && res.run !== undefined) {
      const run: Run = res.run;
      setView((v) => withRun(v, run));
    }
    return res.stopped;
  }, [id]);

  const resume = useCallback(async (): Promise<ResumeOutcome> => {
    try {
      const run = await client.runs.resume.mutate({ id });
      setView((v) => withRun(v, run));
      return { ok: true };
    } catch (err) {
      if (err instanceof TRPCClientError && err.data?.code === 'CONFLICT') {
        const winding = /winding down/i.test(err.message ?? '');
        return {
          ok: false,
          kind: winding ? 'winding-down' : 'refused',
          message: err.message ?? '',
        };
      }
      return { ok: false, kind: 'refused', message: runErrorMessage(err) };
    }
  }, [id]);

  return { view, status, error, streamState, reload, stop, resume };
}

// ── the question round ───────────────────────────────────────────────────────

export type QuestionsStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

export interface UseRunQuestions {
  readonly questions: RunQuestionsResult | null;
  readonly qStatus: QuestionsStatus;
  readonly qError: string | null;
  reload: () => Promise<void>;
  /** Send a batch of answers through the answer door; refreshes the recorded round. */
  submit: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
}

export function useRunQuestions(runId: string, enabled: boolean, refreshKey = ''): UseRunQuestions {
  const [questions, setQuestions] = useState<RunQuestionsResult | null>(null);
  const [qStatus, setQStatus] = useState<QuestionsStatus>('idle');
  const [qError, setQError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setQStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered round
    setQError(null);
    try {
      setQuestions((await client.runs.questions.query({ id: runId })) ?? null);
      setQStatus('ready');
    } catch (err) {
      if (isNotFound(err)) {
        // The run has no question round (none published yet, or none ever) — honest
        // absence, not an error. B1: this hook is now enabled for the run page's whole
        // life, not just at an awaiting-owner stall, so absence is the COMMON case.
        setQuestions(null);
        setQStatus('none');
      } else {
        setQError(runErrorMessage(err));
        setQStatus('error');
      }
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setQuestions(null);
      setQStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE (B1): an auto-approved round with advisories
    // can appear at ANY stage boundary — each status move re-reads the disk truth, the
    // same law as useRunDataModel.
  }, [enabled, refreshKey, reload]);

  const submit = useCallback(
    async (answers: CheckpointAnswer[]): Promise<AnswerRunResult> => {
      const result = await client.runs.answer.mutate({ id: runId, answers });
      await reload(); // refresh the recorded answers; the round document is the truth
      return result;
    },
    [runId, reload],
  );

  return { questions, qStatus, qError, reload, submit };
}

// ── the grounded brief, for every room (the subject strip) ───────────────────

/**
 * THE RUN'S CLAIMS, alone. The intake rides the data-model read surface (the compiled brief
 * is what the ER door approves a model *of*), and the subject strip needs it in whichever room
 * a question landed in — so the shell reads it once and hands it down, rather than every room
 * growing its own copy of the ER fetch.
 *
 * IT IS THE SAME ONE READ the data-model room already makes, so a run that carries no intake
 * (every build made before the intake leg — both of today's) resolves to `undefined` and every
 * strip on the page renders nothing at all. Errors are swallowed to `undefined` ON PURPOSE:
 * this read decorates cards, and a decoration must never turn a room into an error screen.
 */
export function useRunIntake(runId: string, enabled: boolean, refreshKey: string): RunIntakeSurface | undefined {
  const [intake, setIntake] = useState<RunIntakeSurface | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      setIntake(undefined);
      return;
    }
    let live = true;
    void (async () => {
      try {
        const payload = await client.runs.dataModel.query({ id: runId });
        if (live) setIntake(payload?.intake);
      } catch {
        if (live) setIntake(undefined);
      }
    })();
    return () => {
      live = false;
    };
  }, [runId, enabled, refreshKey]);

  return intake;
}

// ── the data model (batch 5: the review screen + the approval door) ──────────

export type DataModelStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseRunDataModel {
  /** The read surface's payload. `dataModel.model === null` is HONEST ABSENCE (the run
   *  has not produced the artifact yet) — 'ready' status, nothing to apologize for. */
  readonly dataModel: RunDataModelResult | null;
  /** What a submitted revision came to (`runs.dataModelDiff`): none / pending /
   *  unchanged / changed / closed. Fetched beside the model, same refresh triggers. */
  readonly diff: RunDataModelDiffResult | null;
  readonly dmStatus: DataModelStatus;
  readonly dmError: string | null;
  reload: () => Promise<void>;
  /** The approval door: record the approval, then refresh the payload. When the server
   *  also resumed the run, the continuation arrives over the SAME SSE stream (the
   *  'started' frame flips the view back to running) — no view surgery here. */
  approve: () => Promise<ApproveDataModelResult>;
  /** The DISAGREE door (batch 6): the person's words go to the chain's owner channel
   *  and the run relaunches to re-derive. The re-derivation arrives over the same SSE
   *  stream; the diff appears at the next halt (the refreshKey re-read picks it up). */
  requestRevision: (comments: RevisionComment[]) => Promise<RequestDataModelRevisionResult>;
  /** The EDIT door's dry run (the edit-door batch): what one exact edit WILL do — computed
   *  server-side against the current bytes, nothing committed, nothing cached here. */
  previewEdit: (edit: DataModelEdit) => Promise<RunDataModelEditPreview>;
  /** The EDIT door: mint the deterministic owner-patch row and relaunch; the edited model
   *  arrives at the next halt over the same SSE stream, with the diff bound to the model
   *  the person read (same machinery as the disagree door's). */
  submitEdit: (edit: DataModelEdit) => Promise<EditDataModelResult>;
  /** The BASKET's dry run: the whole composed list chained IN ORDER against the current
   *  bytes server-side (edit #2 evaluated against the model as #1 left it), nothing
   *  committed, nothing cached here. */
  previewBasket: (edits: DataModelEdit[]) => Promise<RunDataModelBasketPreview>;
  /** The BASKET's apply: all rows in one append, ONE resume — all-or-nothing on any
   *  refusal (the server re-checks; a refused basket lands nothing). */
  submitBasket: (edits: DataModelEdit[]) => Promise<ApplyDataModelBasketResult>;
}

/**
 * `runs.dataModel` + `runs.dataModelDiff`, page-scoped. `refreshKey` re-reads when the
 * run's status moves (a halt landing / a resume starting both change what the disk
 * says) — both payloads are pure functions of the run directory, so re-reading is safe.
 */
export function useRunDataModel(runId: string, enabled: boolean, refreshKey: string): UseRunDataModel {
  const [dataModel, setDataModel] = useState<RunDataModelResult | null>(null);
  const [diff, setDiff] = useState<RunDataModelDiffResult | null>(null);
  const [dmStatus, setDmStatus] = useState<DataModelStatus>('idle');
  const [dmError, setDmError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setDmStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered model
    setDmError(null);
    try {
      const [model, diffPayload] = await Promise.all([
        client.runs.dataModel.query({ id: runId }),
        client.runs.dataModelDiff.query({ id: runId }),
      ]);
      setDataModel(model);
      setDiff(diffPayload);
      setDmStatus('ready');
    } catch (err) {
      setDmError(runErrorMessage(err));
      setDmStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setDataModel(null);
      setDiff(null);
      setDmStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [enabled, refreshKey, reload]);

  const approve = useCallback(async (): Promise<ApproveDataModelResult> => {
    const result = await client.runs.approveDataModel.mutate({ id: runId });
    await reload(); // the approval state on disk is the truth the screen renders
    return result;
  }, [runId, reload]);

  const requestRevision = useCallback(
    async (comments: RevisionComment[]): Promise<RequestDataModelRevisionResult> => {
      const result = await client.runs.requestDataModelRevision.mutate({ id: runId, comments });
      await reload(); // the recorded revision (now 'pending') is disk truth too
      return result;
    },
    [runId, reload],
  );

  const previewEdit = useCallback(
    async (edit: DataModelEdit): Promise<RunDataModelEditPreview> =>
      client.runs.previewDataModelEdit.query({ id: runId, edit }),
    [runId],
  );

  const submitEdit = useCallback(
    async (edit: DataModelEdit): Promise<EditDataModelResult> => {
      const result = await client.runs.editDataModel.mutate({ id: runId, edit });
      await reload(); // the recorded round + the row on disk are the truth the screen renders
      return result;
    },
    [runId, reload],
  );

  const previewBasket = useCallback(
    async (edits: DataModelEdit[]): Promise<RunDataModelBasketPreview> =>
      client.runs.previewDataModelBasket.query({ id: runId, edits }),
    [runId],
  );

  const submitBasket = useCallback(
    async (edits: DataModelEdit[]): Promise<ApplyDataModelBasketResult> => {
      const result = await client.runs.applyDataModelBasket.mutate({ id: runId, edits });
      await reload(); // the recorded round + the rows on disk are the truth the screen renders
      return result;
    },
    [runId, reload],
  );

  return { dataModel, diff, dmStatus, dmError, reload, approve, requestRevision, previewEdit, submitEdit, previewBasket, submitBasket };
}

// ── one stage's review (batch 7: the six read surfaces) ──────────────────────

/** The selected stage's read, tagged so the screen can narrow without re-asking. */
export type StageReviewPayload =
  | { readonly stage: 'statemachine'; readonly read: StageRead<LifecyclesDoc> }
  | { readonly stage: 'process'; readonly read: StageRead<OperationsDoc> }
  | { readonly stage: 'rules'; readonly read: StageRead<RulesDoc> }
  | { readonly stage: 'rbac'; readonly read: StageRead<PermissionsDoc> }
  | { readonly stage: 'audit'; readonly read: StageRead<AuditDoc> }
  | { readonly stage: 'decomposition'; readonly read: StageRead<ModulesDoc> };

export type StageReviewStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseStageReview {
  /** The selected stage's payload. An artifact the run has not produced is a payload
   *  STATE ('absent' / 'unintelligible' — stage-artifacts.ts law), never an error. */
  readonly payload: StageReviewPayload | null;
  /** The selected stage's DOOR (the A3/A4 batch): the signature-bound approval state +
   *  what a submitted revision came to. Fetched beside the read, dropped on a stale
   *  stage switch exactly like the payload. */
  readonly door: RunStageDoorResult | null;
  readonly srStatus: StageReviewStatus;
  readonly srError: string | null;
  reload: () => Promise<void>;
  /** The per-stage approval door: record the approval, then refresh from disk. When the
   *  server also resumed the run, the continuation arrives over the same SSE stream. */
  approve: () => Promise<ApproveStageRouteResult>;
  /** The per-stage DISAGREE door: the person's words go to `guidance/<stage>.md` — the
   *  channel the chain consumes as this stage's own input leg — and the run relaunches
   *  to re-derive the stage. The diff appears at the next halt. */
  requestRevision: (comments: RevisionComment[]) => Promise<RequestStageRevisionResult>;
  /** The per-stage EXACT door (register A2), where the stage's bytes carry one. A change
   *  previewed here is a dry run against the real artifact by the SAME engine the chain
   *  applies with — no model call, and nothing is committed. */
  previewEdit: (edit: StageEditPatch) => Promise<RunStageEditPreview>;
  /** Order ONE exact change: a typed row in `owner-patches.json`, applied at this stage's
   *  boundary on the resume. No prose channel is touched, so no re-derivation is bought. */
  submitEdit: (edit: StageEditPatch) => Promise<OrderStageEditResult>;
  /** The BASKET's chained preview: change k evaluated against the model as changes
   *  1..k-1 left it, so the combined answer is true rather than k separate answers. */
  previewBasket: (edits: StageEditPatch[]) => Promise<RunStageBasketPreview>;
  /** Apply the whole basket, all-or-nothing, in one content-deduped append + one resume. */
  submitBasket: (edits: StageEditPatch[]) => Promise<ApplyStageBasketResult>;
}

async function fetchStageReview(runId: string, stage: StageReviewKey): Promise<StageReviewPayload> {
  // One switch, fully typed per route — the stage keys are the rail's, the routes are
  // the owner-language server names (runs router, batch 7).
  switch (stage) {
    case 'statemachine':
      return { stage, read: await client.runs.lifecycles.query({ id: runId }) };
    case 'process':
      return { stage, read: await client.runs.operations.query({ id: runId }) };
    case 'rules':
      return { stage, read: await client.runs.rules.query({ id: runId }) };
    case 'rbac':
      return { stage, read: await client.runs.permissions.query({ id: runId }) };
    case 'audit':
      return { stage, read: await client.runs.audit.query({ id: runId }) };
    case 'decomposition':
      return { stage, read: await client.runs.modules.query({ id: runId }) };
    default: {
      const exhaustive: never = stage;
      throw new Error(`unknown stage: ${String(exhaustive)}`);
    }
  }
}

/**
 * `runs.<stage read>`, page-scoped and LAZY: nothing is fetched until a person opens a
 * review stage from the rail (`stage` null = idle). `refreshKey` re-reads when the run's
 * status moves — the payload is a pure function of the run directory, so re-reading is
 * safe, and a stage that completes while the person is looking fills in without a
 * remount. A stale response (the person already switched stages) is dropped, never
 * rendered under the wrong heading.
 */
export function useStageReview(runId: string, stage: StageReviewKey | null, refreshKey: string): UseStageReview {
  const [payload, setPayload] = useState<StageReviewPayload | null>(null);
  const [door, setDoor] = useState<RunStageDoorResult | null>(null);
  const [srStatus, setSrStatus] = useState<StageReviewStatus>('idle');
  const [srError, setSrError] = useState<string | null>(null);

  // The LIVE selection, for dropping stale responses: an in-flight fetch for a stage the
  // person has already left must never land under the new stage's heading.
  const stageRef = useRef(stage);
  stageRef.current = stage;

  const reload = useCallback(async () => {
    if (stage === null) return;
    const requested = stage;
    setSrStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered doc
    setSrError(null);
    try {
      const [result, doorResult] = await Promise.all([
        fetchStageReview(runId, requested),
        client.runs.stageDoor.query({ id: runId, stage: requested }),
      ]);
      if (stageRef.current !== requested) return; // stale — the person switched stages
      setPayload(result);
      setDoor(doorResult);
      setSrStatus('ready');
    } catch (err) {
      if (stageRef.current !== requested) return; // a stale failure is not this stage's error
      setSrError(runErrorMessage(err));
      setSrStatus('error');
    }
  }, [runId, stage]);

  useEffect(() => {
    if (stage !== null) {
      setPayload(null); // never show one stage's doc under another stage's heading
      setDoor(null);
      setSrStatus('loading');
      void reload();
    } else {
      setPayload(null);
      setDoor(null);
      setSrStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [stage, refreshKey, reload]);

  const approve = useCallback(async (): Promise<ApproveStageRouteResult> => {
    if (stage === null) throw new Error('no stage selected');
    const result = await client.runs.approveStage.mutate({ id: runId, stage });
    await reload(); // the approval state on disk is the truth the screen renders
    return result;
  }, [runId, stage, reload]);

  const requestRevision = useCallback(
    async (comments: RevisionComment[]): Promise<RequestStageRevisionResult> => {
      if (stage === null) throw new Error('no stage selected');
      const result = await client.runs.requestStageRevision.mutate({ id: runId, stage, comments });
      await reload(); // the recorded revision (now 'pending') is disk truth too
      return result;
    },
    [runId, stage, reload],
  );

  const previewEdit = useCallback(
    async (edit: StageEditPatch): Promise<RunStageEditPreview> => {
      if (stage === null) throw new Error('no stage selected');
      return client.runs.previewStageEdit.query({ id: runId, stage, edit });
    },
    [runId, stage],
  );

  const submitEdit = useCallback(
    async (edit: StageEditPatch): Promise<OrderStageEditResult> => {
      if (stage === null) throw new Error('no stage selected');
      const result = await client.runs.orderStageEdit.mutate({ id: runId, stage, edit });
      await reload(); // the recorded round + the row on disk are the truth the screen renders
      return result;
    },
    [runId, stage, reload],
  );

  const previewBasket = useCallback(
    async (edits: StageEditPatch[]): Promise<RunStageBasketPreview> => {
      if (stage === null) throw new Error('no stage selected');
      return client.runs.previewStageBasket.query({ id: runId, stage, edits });
    },
    [runId, stage],
  );

  const submitBasket = useCallback(
    async (edits: StageEditPatch[]): Promise<ApplyStageBasketResult> => {
      if (stage === null) throw new Error('no stage selected');
      const result = await client.runs.applyStageBasket.mutate({ id: runId, stage, edits });
      await reload();
      return result;
    },
    [runId, stage, reload],
  );

  return {
    payload,
    door,
    srStatus,
    srError,
    reload,
    approve,
    requestRevision,
    previewEdit,
    submitEdit,
    previewBasket,
    submitBasket,
  };
}

// ── the verdict (batch 8: the end-of-run read surface) ───────────────────────

export interface UseRunVerdict {
  /** The verdict read. Absence is a payload STATE ('absent' = the run never composed a
   *  verdict — an honest not-yet; 'unintelligible' = torn or newer than this portal),
   *  never an error (verdict-artifacts.ts law). */
  readonly verdict: StageRead<VerdictDoc> | null;
  readonly vStatus: StageReviewStatus;
  readonly vError: string | null;
  reload: () => Promise<void>;
}

/**
 * `runs.verdict`, page-scoped and LAZY like useStageReview: nothing is fetched until the
 * person opens the CHECKS row from the rail. `refreshKey` re-reads when the run's status
 * moves — a verdict that lands while the person is looking fills in without a remount.
 */
export function useRunVerdict(runId: string, enabled: boolean, refreshKey: string): UseRunVerdict {
  const [verdict, setVerdict] = useState<StageRead<VerdictDoc> | null>(null);
  const [vStatus, setVStatus] = useState<StageReviewStatus>('idle');
  const [vError, setVError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setVStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered doc
    setVError(null);
    try {
      setVerdict(await client.runs.verdict.query({ id: runId }));
      setVStatus('ready');
    } catch (err) {
      setVError(runErrorMessage(err));
      setVStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setVerdict(null);
      setVStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [enabled, refreshKey, reload]);

  return { verdict, vStatus, vError, reload };
}

// ── the build half: the code the machine wrote, and the screens it drew ──────

export interface UseRunCode {
  /** The modules + their files. `state: 'absent'` is an honest not-yet (nothing written). */
  readonly code: CodeModulesRead | null;
  readonly cStatus: StageReviewStatus;
  readonly cError: string | null;
  reload: () => Promise<void>;
}

/**
 * `runs.codeModules`, LAZY like every other room's read: nothing is fetched until a person
 * opens the Code room. `refreshKey` re-reads on each status move — the factory writes
 * modules one at a time, so a room left open while the build runs must fill in.
 */
export function useRunCode(runId: string, enabled: boolean, refreshKey: string): UseRunCode {
  const [code, setCode] = useState<CodeModulesRead | null>(null);
  const [cStatus, setCStatus] = useState<StageReviewStatus>('idle');
  const [cError, setCError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setCStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered list
    setCError(null);
    try {
      setCode(await client.runs.codeModules.query({ id: runId }));
      setCStatus('ready');
    } catch (err) {
      setCError(runErrorMessage(err));
      setCStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setCode(null);
      setCStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [enabled, refreshKey, reload]);

  return { code, cStatus, cError, reload };
}

export type CodeFileStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseCodeFile {
  /** One file as the server read it. A refusal / absence / oversize is a payload STATE
   *  carrying its own sentence — never an error, and never a partial file. */
  readonly file: CodeFileRead | null;
  readonly fStatus: CodeFileStatus;
  readonly fError: string | null;
}

/**
 * `runs.codeFile` for whichever file is open (`null` = none). The path is one the LISTING
 * handed out — the client never composes one — and the server re-jails it anyway.
 *
 * A stale response is dropped, exactly like `useStageReview` drops a stage the person has
 * already left: one file's text must never appear under another file's header.
 */
export function useCodeFile(runId: string, path: string | null): UseCodeFile {
  const [file, setFile] = useState<CodeFileRead | null>(null);
  const [fStatus, setFStatus] = useState<CodeFileStatus>('idle');
  const [fError, setFError] = useState<string | null>(null);

  const pathRef = useRef(path);
  pathRef.current = path;

  useEffect(() => {
    if (path === null) {
      setFile(null);
      setFStatus('idle');
      setFError(null);
      return;
    }
    const requested = path;
    setFile(null); // never show one file's bytes under another file's name
    setFStatus('loading');
    setFError(null);
    void (async () => {
      try {
        const read = await client.runs.codeFile.query({ id: runId, path: requested });
        if (pathRef.current !== requested) return; // stale — the person opened another file
        setFile(read);
        setFStatus('ready');
      } catch (err) {
        if (pathRef.current !== requested) return;
        setFError(runErrorMessage(err));
        setFStatus('error');
      }
    })();
  }, [runId, path]);

  return { file, fStatus, fError };
}

export interface UseRunScreens {
  /** The gallery read. Absence is a payload STATE ('absent' = no screens were generated;
   *  'unintelligible' = a spec this portal cannot read), never an error. */
  readonly screens: StageRead<ScreensDoc> | null;
  readonly scStatus: StageReviewStatus;
  readonly scError: string | null;
  reload: () => Promise<void>;
}

/** `runs.screens`, LAZY and status-refreshed — the same law as every other room's read. */
export function useRunScreens(runId: string, enabled: boolean, refreshKey: string): UseRunScreens {
  const [screens, setScreens] = useState<StageRead<ScreensDoc> | null>(null);
  const [scStatus, setScStatus] = useState<StageReviewStatus>('idle');
  const [scError, setScError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setScStatus((s) => (s === 'ready' ? s : 'loading'));
    setScError(null);
    try {
      setScreens(await client.runs.screens.query({ id: runId }));
      setScStatus('ready');
    } catch (err) {
      setScError(runErrorMessage(err));
      setScStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setScreens(null);
      setScStatus('idle');
    }
  }, [enabled, refreshKey, reload]);

  return { screens, scStatus, scError, reload };
}

export type ScreenPreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseScreenPreview {
  /** ONE drawing as the server read it. 'absent' / 'failed' are payload STATES with their
   *  own sentence — never an error, and never a blank frame. */
  readonly preview: ScreenPreviewRead | null;
  readonly pStatus: ScreenPreviewStatus;
  readonly pError: string | null;
}

/**
 * `runs.screenPreview` for whichever (screen, role) is open (`null` = none). LAZY: a gallery
 * of 42 screens fetches nothing until someone asks for a picture, and each drawing carries the
 * app's own ~25 KB stylesheet, so fetching them all up front would be pointless weight.
 *
 * A stale response is DROPPED, exactly as `useCodeFile` drops a file the person has already
 * left: one screen's drawing must never appear under another screen's label — and here that
 * would be a picture of the wrong thing, which is the worst thing this room could do.
 */
export function useScreenPreview(
  runId: string,
  screenId: string | null,
  role: string | null,
): UseScreenPreview {
  const [preview, setPreview] = useState<ScreenPreviewRead | null>(null);
  const [pStatus, setPStatus] = useState<ScreenPreviewStatus>('idle');
  const [pError, setPError] = useState<string | null>(null);

  const wantedRef = useRef<string | null>(null);
  const wanted = screenId === null || role === null ? null : `${screenId} ${role}`;
  wantedRef.current = wanted;

  useEffect(() => {
    if (screenId === null || role === null) {
      setPreview(null);
      setPStatus('idle');
      setPError(null);
      return;
    }
    const requested = `${screenId} ${role}`;
    setPreview(null); // never show one screen's drawing under another screen's name
    setPStatus('loading');
    setPError(null);
    void (async () => {
      try {
        const read = await client.runs.screenPreview.query({ id: runId, screenId, role });
        if (wantedRef.current !== requested) return; // stale — they opened another picture
        setPreview(read);
        setPStatus('ready');
      } catch (err) {
        if (wantedRef.current !== requested) return;
        setPError(runErrorMessage(err));
        setPStatus('error');
      }
    })();
  }, [runId, screenId, role]);

  return { preview, pStatus, pError };
}

// ── what a build read (the documents record, frozen at launch) ───────────────

export interface UseRunDocuments {
  readonly documents: RunDocumentsResult | null;
  readonly dStatus: StageReviewStatus;
  readonly dError: string | null;
  reload: () => Promise<void>;
}

/**
 * The run's own record of the project's documents: which were read into its brief and
 * which were held, with the reason.
 *
 * It is fetched ONCE per mount and deliberately NOT re-read on every status move (unlike
 * the stage reviews): the record is written at launch and never changes for the life of
 * the run — re-polling it would only invite the screen to imply that a document uploaded
 * since could still join this build. It cannot.
 */
export function useRunDocuments(runId: string, enabled: boolean): UseRunDocuments {
  const [documents, setDocuments] = useState<RunDocumentsResult | null>(null);
  const [dStatus, setDStatus] = useState<StageReviewStatus>('idle');
  const [dError, setDError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setDStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered record
    setDError(null);
    try {
      setDocuments(await client.runs.documents.query({ id: runId }));
      setDStatus('ready');
    } catch (err) {
      setDError(runErrorMessage(err));
      setDStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setDocuments(null);
      setDStatus('idle');
    }
  }, [enabled, reload]);

  return { documents, dStatus, dError, reload };
}

// ── the handover (can this build be taken away, and how) ─────────────────────

export interface UseRunAppPackage {
  readonly appPackage: AppPackageRead | null;
  readonly apStatus: StageReviewStatus;
  readonly apError: string | null;
  reload: () => Promise<void>;
}

/**
 * The run's HANDOVER state via `runs.appPackage`: whether an app exists on disk, what the
 * archive would carry, and the commands to run it — all measured server-side.
 *
 * Re-read on every status move (the `refreshKey` idiom the stage reviews use): the app is
 * written near the very end of a run, so a page open while the build finishes must stop
 * saying "nothing yet" the moment there IS something. Fetched unconditionally with the run
 * — this is not a rail row a person opens, it is the end of the story, and it must be on
 * screen without being hunted for.
 */
export function useRunAppPackage(runId: string, enabled: boolean, refreshKey: string): UseRunAppPackage {
  const [appPackage, setAppPackage] = useState<AppPackageRead | null>(null);
  const [apStatus, setApStatus] = useState<StageReviewStatus>('idle');
  const [apError, setApError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setApStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered state
    setApError(null);
    try {
      setAppPackage(await client.runs.appPackage.query({ id: runId }));
      setApStatus('ready');
    } catch (err) {
      setApError(runErrorMessage(err));
      setApStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setAppPackage(null);
      setApStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [enabled, refreshKey, reload]);

  return { appPackage, apStatus, apError, reload };
}

/**
 * The authorized, same-origin URL the archive downloads from — the documents precedent
 * exactly (documents/hooks.ts `downloadUrl`): a plain path an `<a href>` can point at, so
 * the browser attaches the httpOnly session cookie itself and NO token ever appears in a
 * URL, a log line, or a shared link. The server re-checks ownership on every request.
 */
export function appPackageUrl(runId: string): string {
  return `/api/runs/${encodeURIComponent(runId)}/app.zip`;
}

// ── the recorded orders + the withdraw door (register B23) ───────────────────

export interface UseOwnerEdits {
  /** The attributed ledger history: every ORDER row, active or withdrawn. */
  readonly ownerEdits: RunOwnerEditsResult | null;
  readonly oeStatus: StageReviewStatus;
  readonly oeError: string | null;
  reload: () => Promise<void>;
  /** Withdraw one recorded order (by its ledger position), then refresh the list —
   *  the row on disk is the truth the screen renders. */
  withdraw: (index: number) => Promise<RetractEditResult>;
}

/**
 * `runs.ownerEdits` + `runs.retractEdit`, page-scoped. Re-read on every status move (the
 * `refreshKey` idiom): answers and edit doors mint rows mid-run, and a withdrawal changes
 * which rows are in force.
 */
export function useOwnerEdits(runId: string, enabled: boolean, refreshKey: string): UseOwnerEdits {
  const [ownerEdits, setOwnerEdits] = useState<RunOwnerEditsResult | null>(null);
  const [oeStatus, setOeStatus] = useState<StageReviewStatus>('idle');
  const [oeError, setOeError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setOeStatus((s) => (s === 'ready' ? s : 'loading')); // a re-read must not blank a rendered list
    setOeError(null);
    try {
      setOwnerEdits(await client.runs.ownerEdits.query({ id: runId }));
      setOeStatus('ready');
    } catch (err) {
      setOeError(runErrorMessage(err));
      setOeStatus('error');
    }
  }, [runId]);

  useEffect(() => {
    if (enabled) {
      void reload();
    } else {
      setOwnerEdits(null);
      setOeStatus('idle');
    }
    // refreshKey is a dependency ON PURPOSE: each status move re-reads the disk truth.
  }, [enabled, refreshKey, reload]);

  const withdraw = useCallback(
    async (index: number): Promise<RetractEditResult> => {
      const result = await client.runs.retractEdit.mutate({ id: runId, index });
      await reload(); // the rows on disk are the truth the screen renders
      return result;
    },
    [runId, reload],
  );

  return { ownerEdits, oeStatus, oeError, reload, withdraw };
}

// ── a business's runs, and one app's ─────────────────────────────────────────
//
// TWO LISTS, because two questions are being asked. `useProjectRuns` answers "is anything
// building for this business right now?" — the one-live-run-per-business gate the project
// page shows proactively, which spans every app. `useAppRuns` answers "what has THIS app
// been through?" — the build history of one app, and only that app: a run that predates
// apps belongs to none and appears in neither app's list (the project list still has it).

export type ProjectRunsStatus = 'loading' | 'ready' | 'error';

export interface UseProjectRuns {
  readonly runs: Run[];
  readonly status: ProjectRunsStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
}

export function useProjectRuns(projectId: string): UseProjectRuns {
  const [runs, setRuns] = useState<Run[]>([]);
  const [status, setStatus] = useState<ProjectRunsStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setRuns(await client.runs.list.query({ projectId }));
      setStatus('ready');
    } catch (err) {
      setError(runErrorMessage(err));
      setStatus('error');
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { runs, status, error, reload };
}

/** One app's builds, newest first — the app page's history and its live-run signal. */
export function useAppRuns(appId: string): UseProjectRuns {
  const [runs, setRuns] = useState<Run[]>([]);
  const [status, setStatus] = useState<ProjectRunsStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setRuns(await client.runs.list.query({ appId }));
      setStatus('ready');
    } catch (err) {
      setError(runErrorMessage(err));
      setStatus('error');
    }
  }, [appId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { runs, status, error, reload };
}
